'use client';
import React, { useEffect, useRef, useState } from 'react';
import './Samples.css';
import { observer } from 'mobx-react';
import samplesModel, { SAMPLES_MODE } from './SamplesModel';
import SamplesSelector from './SamplesSelector/SamplesSelector';
import SamplesEditor from './SamplesEditor/SamplesEditor';
import SamplesUploader from './SamplesUploader/SamplesUploader';
import AlbumDetailView from './Albums/AlbumDetailView';
import samplesDiagnosticPopupModel from './SamplesDiagnosticPopup/SamplesDiagnosticPopupModel';
import samplesSelectorModel from './SamplesSelector/SamplesSelectorModel';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { PiPlaylistThin } from 'react-icons/pi';
import { IconButton } from '../ui/button';
import { useTour } from '@reactour/tour';
import { useTranslation } from 'react-i18next';
import TooltipSamples from '../Tooltips/TooltipSamples';
import { usePathname } from 'next/navigation';

export const Samples = observer(() => {
  const ref = useRef<HTMLDivElement>(null);
  // const { currentStep, setCurrentStep, isOpen: isTourOpen } = useTour();
  const [popoverOpen, setPopoverOpen] = useState(false);
  const { currentStep, setCurrentStep, isOpen: isTourOpen } = useTour();
  const { t } = useTranslation();
  const pathname = usePathname();

  const currentMode = samplesModel.currentMode;
  const isDebugPage = pathname === '/debug' || pathname === '/debug/';

  samplesModel.ref = ref;

  if (currentMode === SAMPLES_MODE.SELECT || currentMode === SAMPLES_MODE.ALBUMS || currentMode === SAMPLES_MODE.PLAYLISTS) {
    samplesDiagnosticPopupModel.stopMessageListening();
    samplesDiagnosticPopupModel.resetWarnings();
  }

  const handlePopoverOpenChange = (open: boolean) => {
    setPopoverOpen(open);
    // If tour is running and we're on step 0 (samples menu button),
    // and popover just opened, advance to step 1
    if (isTourOpen && currentStep === 0 && open) {
      setTimeout(() => {
        setCurrentStep(1);
      }, 300); // Small delay to ensure popover content is rendered
    }
  };

  // Listen for when tour advances from step 1 to 2 and close popover
  useEffect(() => {
    if (isTourOpen && currentStep === 2 && popoverOpen) {
      setTimeout(() => {
        setPopoverOpen(false);
      }, 500); // Small delay to let user see the selection
    }
  }, [currentStep, isTourOpen, popoverOpen]);

  // Listen for programmatic control of samples menu
  useEffect(() => {
    if (samplesSelectorModel.samplesMenuOpen !== null) {
      setPopoverOpen(samplesSelectorModel.samplesMenuOpen);
      // Reset to null after applying (use setTimeout to avoid state update conflicts)
      setTimeout(() => {
        samplesSelectorModel.setSamplesMenuOpen(null);
      }, 0);
    }
  }, [samplesSelectorModel.samplesMenuOpen]);

  return (
    <>
      <Popover open={popoverOpen} onOpenChange={handlePopoverOpenChange}>
        <TooltipSamples>
          <PopoverTrigger asChild>
            <IconButton
              data-testid='open_samples_menu_btn'
              aria-label={t('tours.mainTour.open_samples_menu')}
              suppressHydrationWarning
            >
              {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
              {/* @ts-ignore-error */}
              <PiPlaylistThin size={36} />
            </IconButton>
          </PopoverTrigger>
        </TooltipSamples>
        <PopoverContent className={`w-[calc(100vw-20px)] max-w-[400px] ${isDebugPage ? 'max-h-[50vh]' : 'max-h-[calc(100vh-180px)]'} flex flex-col border-1 rounded-md overscroll-contain`}>
          {(currentMode === SAMPLES_MODE.SELECT || currentMode === SAMPLES_MODE.ALBUMS || currentMode === SAMPLES_MODE.PLAYLISTS) && <SamplesSelector />}
          {currentMode === SAMPLES_MODE.UPLOAD && <SamplesUploader />}
          {currentMode === SAMPLES_MODE.EDIT && <SamplesEditor />}
          {currentMode === SAMPLES_MODE.ALBUM_DETAIL && <AlbumDetailView />}
        </PopoverContent>
      </Popover>
    </>
  );
});
